import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, Home, RefreshCw, Mail, Loader2, Check, Copy } from 'lucide-react';
import { generateFailureCode } from '@/lib/errorToast';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCode: string | null;
  sending: boolean;
  reportSent: boolean;
  copied: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorCode: null,
    sending: false,
    reportSent: false,
    copied: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorCode = generateFailureCode();
    
    this.setState({
      error,
      errorInfo,
      errorCode,
    });
    
    // Log to database
    import('@/lib/errorLogger').then(({ logReactError }) => {
      logReactError(error, errorInfo, errorCode);
    });
  }

  private handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null, 
      errorCode: null,
      sending: false,
      reportSent: false,
      copied: false,
    });
  };

  private handleCopyErrorCode = async () => {
    if (this.state.errorCode) {
      await navigator.clipboard.writeText(this.state.errorCode);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }
  };

  private handleSendReport = async () => {
    const { error, errorInfo, errorCode } = this.state;
    
    if (!error || !errorCode) return;

    this.setState({ sending: true });

    try {
      const { error: fnError } = await supabase.functions.invoke('send-error-report', {
        body: {
          errorCode,
          errorMessage: error.toString(),
          errorStack: error.stack,
          componentStack: errorInfo?.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
      });

      if (fnError) throw fnError;

      this.setState({ reportSent: true, sending: false });
      toast({
        title: "Error report sent",
        description: "Our technical team has received the error details.",
      });
    } catch (err) {
      this.setState({ sending: false });
      toast({
        title: "Failed to send report",
        description: "Please try again or contact support directly.",
        variant: "destructive",
      });
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
          <Card className="max-w-lg w-full shadow-card border-0 bg-gradient-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-destructive" />
                </div>
                <CardTitle className="text-h4">Something went wrong</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  We're sorry, but something unexpected happened. Our team has been notified and we're working to fix it.
                </p>

                {this.state.errorCode && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <span className="text-caption text-muted-foreground">Error Code:</span>
                    <code className="px-2 py-1 bg-background rounded font-mono text-body-sm font-semibold">
                      {this.state.errorCode}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 ml-auto"
                      onClick={this.handleCopyErrorCode}
                    >
                      {this.state.copied ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                )}

                {this.state.error && (
                  <details className="mt-4">
                    <summary className="text-body-sm text-muted-foreground cursor-pointer hover:text-foreground">
                      Technical details
                    </summary>
                    <div className="mt-2 p-3 bg-muted rounded-md text-micro font-mono overflow-auto">
                      <p className="font-bold mb-1">{this.state.error.toString()}</p>
                      {this.state.errorInfo && (
                        <pre className="whitespace-pre-wrap text-micro mt-2">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <Button 
                  onClick={this.handleSendReport} 
                  disabled={this.state.reportSent || this.state.sending}
                  className="w-full"
                >
                  {this.state.sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending Report...
                    </>
                  ) : this.state.reportSent ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Report Sent
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Error Report
                    </>
                  )}
                </Button>

                <div className="flex gap-3">
                  <Button onClick={this.handleReset} variant="outline" className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button asChild variant="outline" className="flex-1">
                    <a href="/home">
                      <Home className="w-4 h-4 mr-2" />
                      Go Home
                    </a>
                  </Button>
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-body-sm text-muted-foreground text-center">
                  Need help?{' '}
                  <a href="/support-center" className="text-primary hover:underline">
                    Contact Support
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
